import { useState, type ReactNode } from "react";
import { Tooltip } from "@/components/tooltip";
import { RemovableChip } from "@/components/removable-chip";
import {
  CollectionChipIcon,
  DataSourceChipIcon,
  DataSourcePopup,
  findCollection,
  isCollectionId,
  shortNameFor,
  type DataSourceItem,
} from "@/components/watchlists";
import { FilterCard, FilterLabel } from "./primitives";
import { MutedSearchTrigger } from "./muted-search-trigger";

type SourceChipDisplay =
  | { id: string; kind: "source"; item: DataSourceItem; label: string; longName: string }
  | { id: string; kind: "collection"; name: string };

function ChipWithTooltip({
  name,
  description,
  icon,
  children,
}: {
  name: string;
  description?: ReactNode;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Tooltip icon={icon} title={name} description={description} align="start">
      {children}
    </Tooltip>
  );
}

export function DataSourceCard({
  selected,
  onChange,
  items = [],
  className = "flex-1 w-full min-h-41",
  label = "Watchlists",
  helperText = "If no watchlists are selected, all sanctions lists will be searched by default.",
  defaultSelected = [],
  chipDescriptions,
  preventEmpty = false,
  pepAllowed = true,
  adverseMediaEnabled = true,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  /** Catalog to render — live `/sources/` items when provided, else the bundled set. */
  items?: DataSourceItem[];
  className?: string;
  /** Card label. Defaults to "Watchlists"; Single Screening passes "Watchlist" per Figma 15823-70509. */
  label?: string;
  /** Empty-state helper copy. Overridable so screening can use the Figma "all watchlists" wording without changing other callers. */
  helperText?: string;
  /** Selection to restore when the user clicks Reset in the popup (and, when
   *  `preventEmpty` is set, when the last selection is removed). Defaults to empty. */
  defaultSelected?: string[];
  /** Extra tooltip body per selected id (e.g. the Adverse Media chip showing the
   *  company's configured countries/keywords/languages). Rendered below the chip name. */
  chipDescriptions?: Record<string, ReactNode>;
  /**
   * When true, removing the last chip (or applying an empty popup selection)
   * restores `defaultSelected` instead of allowing zero watchlists — Single
   * Screening always needs at least one source to search. Other callers (e.g.
   * monitoring) keep the default: empty is allowed and validated separately.
   */
  preventEmpty?: boolean;
  /** Company entitlements — hide the PEP Lists / Adverse Media collections from the picker when not allowed. */
  pepAllowed?: boolean;
  adverseMediaEnabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const restoreOnEmpty = preventEmpty && defaultSelected.length > 0;

  function handleChange(next: string[]) {
    onChange(restoreOnEmpty && next.length === 0 ? defaultSelected : next);
  }

  const selectedItems: SourceChipDisplay[] = selected
    .map<SourceChipDisplay | null>((id) => {
      if (isCollectionId(id)) {
        const c = findCollection(id);
        return c ? { id, kind: "collection", name: c.label } : null;
      }
      const s = items.find((x) => x.id === id);
      return s
        ? {
            id,
            kind: "source",
            item: s,
            label: shortNameFor(s),
            longName: s.name,
          }
        : null;
    })
    .filter((x): x is SourceChipDisplay => x !== null);

  return (
    <FilterCard className={className}>
      <FilterLabel>{label}</FilterLabel>
      <MutedSearchTrigger
        placeholder="Search"
        count={selected.length}
        unit="watchlist"
        onClick={() => setOpen(true)}
        buttonRef={setAnchor}
      />
      {selected.length === 0 && (
        <div className="w-full rounded-sm bg-grey-100 p-2">
          <p className="text-tiny font-medium text-grey-900">{helperText}</p>
        </div>
      )}
      {selectedItems.length > 0 && (
        <div className="flex max-h-34 w-full flex-wrap items-start gap-1 overflow-y-auto">
          {selectedItems.map((item) => {
            const removeLabel = item.kind === "source" ? item.longName : item.name;
            // Removing this chip would just bounce right back (preventEmpty):
            // it's the sole selection AND it IS the default — hide the no-op
            // X (onRemove omitted). A lone non-default chip (e.g. only
            // "OFAC") still shows it, since removing it is a real change
            // (swaps back to the default), not a no-op.
            const isNoOpRemoval =
              restoreOnEmpty &&
              selected.length === 1 &&
              defaultSelected.length === 1 &&
              item.id === defaultSelected[0];
            const chip = (
              <RemovableChip
                icon={
                  item.kind === "source" ? (
                    <DataSourceChipIcon item={item.item} />
                  ) : (
                    <CollectionChipIcon id={item.id} />
                  )
                }
                removeLabel={removeLabel}
                onRemove={
                  isNoOpRemoval
                    ? undefined
                    : () => handleChange(selected.filter((id) => id !== item.id))
                }
              >
                {item.kind === "source" ? item.label : item.name}
              </RemovableChip>
            );
            return (
              <ChipWithTooltip
                key={item.id}
                name={removeLabel}
                description={chipDescriptions?.[item.id]}
                icon={
                  item.kind === "source" ? (
                    <DataSourceChipIcon item={item.item} size={24} />
                  ) : (
                    <CollectionChipIcon id={item.id} size={24} />
                  )
                }
              >
                {chip}
              </ChipWithTooltip>
            );
          })}
        </div>
      )}
      {open && (
        <DataSourcePopup
          anchor={anchor}
          items={items}
          initialSelected={selected}
          defaultSelected={defaultSelected}
          pepAllowed={pepAllowed}
          adverseMediaEnabled={adverseMediaEnabled}
          onApply={(ids) => {
            handleChange(ids);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </FilterCard>
  );
}
